import type VConsole from "vconsole";

const CAPTURED_CONSOLE_METHODS = ["log", "info", "debug", "warn", "error"] as const;
const DEFAULT_MAX_ENTRIES = 1000;

type CapturedConsoleMethod = typeof CAPTURED_CONSOLE_METHODS[number];
type ConsoleMethod = (...args: unknown[]) => void;
type ConsoleLike = Pick<Console, CapturedConsoleMethod | "clear">;

export interface MobileConsoleLogBuffer {
  entries: readonly string[];
  clear(): void;
  push(method: CapturedConsoleMethod, args: unknown[]): void;
  toText(): string;
}

export function formatConsoleValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.stack ?? `${value.name}: ${value.message}`;
  if (typeof value === "undefined") return "undefined";
  if (typeof value === "bigint") return `${value}n`;
  if (typeof value === "function") return value.toString();

  const seen = new WeakSet<object>();
  try {
    const serialized = JSON.stringify(value, (_key, nestedValue: unknown) => {
      if (typeof nestedValue === "bigint") return `${nestedValue}n`;
      if (typeof nestedValue === "object" && nestedValue !== null) {
        if (seen.has(nestedValue)) return "[Circular]";
        seen.add(nestedValue);
      }
      return nestedValue;
    });
    return serialized ?? String(value);
  } catch {
    return String(value);
  }
}

export function createMobileConsoleLogBuffer(maxEntries = DEFAULT_MAX_ENTRIES): MobileConsoleLogBuffer {
  const entries: string[] = [];
  return {
    get entries() {
      return entries;
    },
    clear() {
      entries.length = 0;
    },
    push(method, args) {
      const timestamp = new Date().toISOString();
      entries.push(`[${timestamp}] ${method.toUpperCase()}: ${args.map(formatConsoleValue).join(" ")}`);
      if (entries.length > maxEntries) entries.splice(0, entries.length - maxEntries);
    },
    toText() {
      return entries.join("\n");
    },
  };
}

export function captureMobileConsoleLogs(
  consoleLike: ConsoleLike,
  buffer: MobileConsoleLogBuffer,
): () => void {
  const originals = new Map<CapturedConsoleMethod, ConsoleMethod>();
  for (const method of CAPTURED_CONSOLE_METHODS) {
    const original = consoleLike[method] as ConsoleMethod;
    originals.set(method, original);
    consoleLike[method] = ((...args: unknown[]) => {
      buffer.push(method, args);
      original.apply(consoleLike, args);
    }) as Console[CapturedConsoleMethod];
  }

  const originalClear = consoleLike.clear;
  consoleLike.clear = (() => {
    buffer.clear();
    originalClear.call(consoleLike);
  }) as Console["clear"];

  return () => {
    for (const [method, original] of originals) consoleLike[method] = original as Console[CapturedConsoleMethod];
    consoleLike.clear = originalClear;
  };
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Clipboard access is unavailable.");
}

function downloadLogs(text: string): void {
  const link = document.createElement("a");
  link.download = `rising-pixel-console-${new Date().toISOString().replace(/[:.]/g, "-")}.log`;
  link.href = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  link.click();
  URL.revokeObjectURL(link.href);
}

export function addMobileConsoleExportPlugin(vConsole: VConsole, buffer: MobileConsoleLogBuffer): void {
  const VConsoleConstructor = vConsole.constructor as typeof VConsole;
  const plugin = new VConsoleConstructor.VConsolePlugin("rising-pixel-console-export", "Export");
  plugin.on("renderTab", (render: (html: string) => void) => {
    render("<div>Use the tools below to copy or download the captured console output.</div>");
  });
  plugin.on("addTool", (addTools: (tools: Array<{ name: string; onClick: () => void }>) => void) => {
    addTools([
      {
        name: "Copy all",
        onClick: () => {
          void copyToClipboard(buffer.toText());
        },
      },
      {
        name: "Download",
        onClick: () => downloadLogs(buffer.toText()),
      },
    ]);
  });
  vConsole.addPlugin(plugin);
}
